import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SectionTitle } from 'modules/shared/components';
import { Table } from 'modules/shared/components/data-display';
import useAsyncData from 'modules/shared/hooks/useAsyncData';
import { useCallback, useMemo } from 'react';
import { LeaveBalanceRow, getLeaveBalance, getMyLeaveBalance } from '../people.api';

const days = (value: unknown) =>
  value === null || value === undefined ? '—' : `${Number(value)} d`;

/**
 * What this employee has left, by leave type, for the current financial year.
 *
 * Nothing showed a balance before: `leaves.is_paid` existed but no quota and no accrual, so
 * HR approved leave without knowing whether it was someone's third casual day of the year or
 * their thirtieth. The figures are derived from the leave rows themselves, never stored.
 */
export default function LeaveBalanceCard({
  employeeId,
  /** `self` reads through the ownership-checked employee route instead of the HR one. */
  scope = 'hr',
  title = 'LEAVE BALANCE',
}: {
  employeeId: number | string | null | undefined;
  scope?: 'hr' | 'self';
  title?: string;
}) {
  const fetcher = useCallback(
    () =>
      !employeeId
        ? Promise.resolve([] as LeaveBalanceRow[])
        : scope === 'self'
          ? getMyLeaveBalance(employeeId)
          : getLeaveBalance(employeeId),
    [employeeId, scope]
  );

  const { data, loading, error, refetch } = useAsyncData<LeaveBalanceRow[]>(fetcher, [], {
    enabled: Boolean(employeeId),
    code: 'LEAVE_BALANCE_FAILED',
    errorMessage: 'Failed to load the leave balance',
    toastOnError: false,
  });

  const columns = useMemo(
    () => [
      {
        id: 'label',
        dataKey: 'label',
        label: 'Leave type',
        align: 'left' as const,
        renderCell: (value: unknown, row: LeaveBalanceRow) => (
          <Stack direction='row' gap={1} alignItems='center'>
            <span>{String(value)}</span>
            {!row.is_paid && <Chip size='small' label='Unpaid' variant='outlined' />}
          </Stack>
        ),
      },
      {
        id: 'entitled',
        dataKey: 'entitled',
        label: 'Entitled',
        align: 'center' as const,
        renderCell: (value: unknown, row: LeaveBalanceRow) => (row.tracked ? days(value) : '—'),
      },
      { id: 'taken', dataKey: 'taken', label: 'Taken', align: 'center' as const, renderCell: days },
      {
        id: 'pending',
        dataKey: 'pending',
        label: 'Pending',
        align: 'center' as const,
        renderCell: days,
      },
      {
        id: 'available',
        dataKey: 'available',
        label: 'Available',
        align: 'center' as const,
        renderCell: (value: unknown, row: LeaveBalanceRow) => {
          if (!row.tracked) return <Chip size='small' label='Not tracked' variant='outlined' />;
          const left = Number(value ?? 0);
          return (
            <Chip
              size='small'
              label={days(value)}
              color={left <= 0 ? 'error' : left <= 2 ? 'warning' : 'success'}
            />
          );
        },
      },
    ],
    []
  );

  if (!employeeId) return null;

  return (
    <Stack gap={2}>
      <SectionTitle title={title} />

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Table
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage='No leave types configured'
      />

      <Typography variant='body2' color='text.secondary'>
        Balances cover the current financial year. Types with no quota — leave without pay,
        compensatory off — are always available and are not counted down.
      </Typography>
    </Stack>
  );
}
